/**
 * #2096 — the live chat data layer. Replaces #2091's invented data with the
 * real cs3-chat service.
 *
 * ══ THE SHAPE OF IT ══
 *
 *   CS3  POST /api/v2/chat/handoff        -> short-lived signed handoff
 *   CHAT POST /api/session/handoff        -> firebase token + 12h session token
 *                                            + this silo's Firebase web config
 *   Firebase signInWithCustomToken        -> the browser can now READ Firestore
 *   Firestore onSnapshot                  -> messages arrive
 *   CHAT POST /api/channels/{id}/messages -> messages leave
 *
 * ⚠ THE BROWSER NEVER WRITES TO FIRESTORE (spec §4). Posting goes through the
 * chat service's HTTP API so nothing can bypass sanitising, the permission
 * check, the RDS log or the billing count — and the Firestore rules deny
 * client writes outright, so a "shortcut" here would simply fail. Reads come
 * straight from Firestore because that is the live delivery channel.
 *
 * ══ TWO TOKENS, TWO ORIGINS, AND THEY MUST NOT CROSS ══
 *
 * The CS3 bearer belongs to CS3 and the chat session token belongs to the chat
 * service. `src/api.js`'s axios instance attaches the CS3 bearer plus the
 * cs3-context-* headers to EVERY request it makes — so calling the chat origin
 * through it would hand a third-party host a live CS3 session token. Every call
 * to the chat service in this file therefore goes through `chatHttp()`, a bare
 * axios instance that knows only the chat session token.
 *
 * ══ WHAT IS NOT HERE ══
 *
 * No settings writes (Configuration → Chat still edits the #2091 preview store
 * until its own issue), no membership sync (spec §8, a server-side job), no
 * FCM. Those are the chat service's #7-#18 and CS3's own follow-ups.
 */

import axios from 'axios';
import api from './api';
import {
    ChatUnavailable, MAX_MESSAGE_CHARS, MAX_SCROLLBACK, PAGE_SIZE,
    applyChannelDoc, clampScrollback, colourFor, initialsFor,
    normaliseChannel, normaliseMessage,
} from './chatShapes.js';

/*
 * Re-exported so callers have one import for the whole data layer and do not
 * need to know which half a given name lives in.
 */
export {
    ChatUnavailable, MAX_MESSAGE_CHARS, MAX_SCROLLBACK, PAGE_SIZE,
    applyChannelDoc, clampScrollback, colourFor, initialsFor,
    normaliseChannel, normaliseMessage,
};

/*
 * One session per page. The handoff is single-use and the session token lasts
 * 12 hours, so re-handing-off on every call would be both wasteful and a
 * guaranteed replay rejection if two calls raced.
 */
let _session = null;
let _signingIn = null;

/*
 * Bumped by every reset. An in-flight sign-in compares the generation it
 * started in against this before publishing itself — see signIn().
 */
let _generation = 0;

/** Test seam, and what a sign-out would call. */
export function resetChatSession() {
    if (_session) {
        // Leave no listener attached to a session nobody owns any more — a
        // live Firestore listener keeps billing reads whether or not anything
        // is reading them (spec §12).
        (_session.unsubscribers || []).forEach((fn) => { try { fn(); } catch (e) { /* already gone */ } });
    }
    _session = null;
    _signingIn = null;
    /*
     * ⚠ INVALIDATES ANY SIGN-IN STILL IN FLIGHT.
     *
     * Without this, a reset during the handoff is ignored by the attempt that
     * is already running: it goes on to initialise Firebase, sign in, and
     * publish itself to `_session` — leaving an authenticated app and a cached
     * session that no component owns. The next mount then gets that session
     * straight from the cache, and if the user switched client in between it
     * carries the PREVIOUS cid, which is the value the service bills to.
     */
    _generation += 1;
}

export function currentSession() {
    return _session;
}

/**
 * Ask CS3 for a handoff.
 *
 * Goes through the app's own axios instance precisely BECAUSE it attaches the
 * CS3 bearer — this one is a CS3 endpoint. It is also the only call in this
 * file that uses it.
 */
export async function requestHandoff() {
    const res = await api.post('chat/handoff');
    const data = (res && res.data && res.data.data) || {};

    if (!data.token || !data.api_url) {
        throw new ChatUnavailable('cs3_handoff_incomplete');
    }

    return data;
}

/**
 * Redeem the handoff at the chat service.
 *
 * ⚠ NO CS3 CREDENTIAL ON THIS REQUEST. The handoff token IS the credential
 * (that is why it lives 90 seconds and is single-use), and the chat service's
 * handoff route is deliberately unauthenticated. Sending the CS3 bearer as
 * well would leak it to another host for no gain.
 */
export async function redeemHandoff(apiUrl, token) {
    try {
        const res = await axios.post(apiUrl + '/api/session/handoff', { token }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 15000,
        });

        return (res && res.data && res.data.data) || {};
    } catch (e) {
        /*
         * The chat service answers 401 `invalid_handoff_token` for EVERY
         * rejection — expired, tampered, wrong silo, already redeemed — on
         * purpose: telling an attacker which one it was confirms a token was
         * genuine. So there is nothing to inspect here, and the only correct
         * response to all of them is to ask CS3 for a new handoff.
         */
        throw new ChatUnavailable(status(e) === 401 ? 'handoff_rejected' : 'chat_unreachable', e);
    }
}

/**
 * Sign in: handoff -> redeem -> Firebase. Returns the session, or throws.
 *
 * Concurrent callers share one attempt. Two panels (docked and the #2091
 * pop-out) mount at once, and two handoffs racing means one of them is
 * redeemed second and refused as a replay.
 */
export function signIn() {
    if (_session) return Promise.resolve(_session);
    if (_signingIn) return _signingIn;

    const startedIn = _generation;

    _signingIn = (async () => {
        const handoff = await requestHandoff();
        const redeemed = await redeemHandoff(handoff.api_url, handoff.token);

        if (!redeemed.session_token) {
            throw new ChatUnavailable('chat_session_missing');
        }

        const session = {
            apiUrl: handoff.api_url,
            silo: handoff.silo,
            sessionToken: redeemed.session_token,
            expiresAt: Date.now() + (Number(redeemed.expires_in) || 0) * 1000,
            // The CA's master switch, already resolved server-side. With chat
            // off the panel must not render at all (spec §3 rule 1).
            chatVisible: redeemed.chat_visible !== false,
            uid: redeemed.uid,
            role: redeemed.role,
            cid: redeemed.cid,
            name: redeemed.name,
            // From CS3's handoff, not the chat service: CS3 owns client names.
            clientName: handoff.client_name || null,
            firebaseConfig: redeemed.firebase || null,
            firebaseToken: redeemed.firebase_token || null,
            firebaseApp: null,
            firestore: null,
            unsubscribers: [],
        };

        // Only sign in to Firebase when there is something to read. A client
        // whose chat is off gets a session (so the panel can say why) and no
        // listener.
        if (session.chatVisible && session.firebaseToken && session.firebaseConfig) {
            await attachFirebase(session);
        }

        // Someone reset us while this was in flight — most likely the panel
        // unmounting, possibly a client switch. Publishing now would install a
        // session nobody asked for; see resetChatSession().
        if (startedIn !== _generation) {
            throw new ChatUnavailable('chat_sign_in_abandoned');
        }

        _session = session;

        return session;
    })();

    const attempt = _signingIn;

    // Release the latch whichever way it went, so a failure can be retried
    // rather than cached forever. Guarded on identity: a reset during the
    // attempt must not have its fresh latch cleared by this one finishing.
    const release = () => { if (_signingIn === attempt) _signingIn = null; };
    attempt.then(release, release);

    return attempt;
}

/**
 * Initialise Firebase for THIS silo and sign in with the custom token.
 *
 * ⚠ The config comes from the handoff response, never from the bundle. A .ca
 * user must land on the Canadian project, and stage- must land on
 * `callsteward-chat-com-stage` and never on a production chat project
 * (spec decision #31) — the server decides which, and the browser is not told
 * in advance which one it is talking to.
 *
 * Lazy-loaded with the same `webpackChunkName: "firebase"` as
 * `utils/firebase-messaging.js`, so the Firestore SDK is not in the main
 * bundle for the users who never open chat.
 */
async function attachFirebase(session) {
    const { initializeApp, getApps } = await import(/* webpackChunkName: "firebase" */ 'firebase/app');
    const { getAuth, signInWithCustomToken } = await import(/* webpackChunkName: "firebase" */ 'firebase/auth');
    const { getFirestore } = await import(/* webpackChunkName: "firebase" */ 'firebase/firestore');

    /*
     * A NAMED app, not the default one. `utils/firebase-messaging.js` already
     * calls initializeApp() for FCM against a DIFFERENT project, and a second
     * initializeApp() with no name either throws or silently returns the first
     * app — which would have chat reading the wrong project's Firestore.
     */
    const NAME = 'cs3-chat';
    const existing = getApps().find((a) => a.name === NAME);
    session.firebaseApp = existing || initializeApp(session.firebaseConfig, NAME);

    const auth = getAuth(session.firebaseApp);
    await signInWithCustomToken(auth, session.firebaseToken);

    session.firestore = getFirestore(session.firebaseApp);
}

/** A bare axios call to the chat service, carrying only its own session token. */
function chatHttp(session, method, path, body) {
    return axios({
        method,
        url: session.apiUrl + path,
        data: body,
        timeout: 15000,
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + session.sessionToken,
        },
    });
}

/**
 * The channels this user can see.
 *
 * ⚠ The channel list comes from the SERVER, and the panel does not build
 * channel ids itself. The id format is the chat service's (spec §7:
 * `c<clientId>_<kind>`, `g_<key>`, `cs_admin`) and deriving it client-side
 * would be a second, drifting implementation of someone else's key.
 */
export async function fetchChannels(session) {
    const path = session.cid
        ? '/api/clients/' + encodeURIComponent(session.cid) + '/channels'
        // CS staff have no client and read their silo's list instead.
        : '/api/channels';

    try {
        const res = await chatHttp(session, 'get', path);

        return ((res && res.data && res.data.data) || []).map(normaliseChannel);
    } catch (e) {
        const code = status(e);

        if (code === 401) throw new ChatUnavailable('chat_session_expired', e);

        /*
         * ⚠ 404 HERE IS THE UUID/INTEGER CONTRACT GAP, NOT A MISSING SERVER.
         *
         * cs3-chat scopes this route with `->whereNumber('client')`, so a CS3
         * client id — a UUID — does not match the route at all. Measured
         * against the live stage service on 2026-10-18:
         *
         *     GET /api/clients/7/channels                      -> 401
         *     GET /api/clients/<uuid>/channels                 -> 404
         *
         * Given its own code it is the distinction worth keeping: lumping it in
         * with 'chat_unreachable' would send whoever sees it looking at DNS,
         * the ALB and the chat box, none of which are wrong. See
         * `App\Chat\ChatHandoff::cid` for the whole picture.
         */
        if (code === 404) throw new ChatUnavailable('chat_client_not_addressable', e);
        // The service refused the list for this actor — a permission answer,
        // not a network one, and it must not read as "chat is down".
        if (code === 403) throw new ChatUnavailable('chat_not_permitted', e);

        throw new ChatUnavailable('chat_unreachable', e);
    }
}

/**
 * Listen to each channel's own Firestore DOCUMENT.
 *
 * ⚠ THIS, NOT A MESSAGE LISTENER, IS WHAT DRIVES THE CHANNEL LIST AND THE
 * TOASTS — spec §7: "Each panel listens to its channels' docs to drive
 * toasts". The distinction is the whole cost model:
 *
 *   channel doc        1 read to attach, 1 per new message. Spec §12's
 *                      "1 read per channel listener attached".
 *   messages, limit 50 about 50 reads to attach. Spec §12 prices that
 *                      separately, as "opening a channel".
 *
 * Attaching a 50-message listener to every channel a user belongs to would
 * therefore cost ~50x what the model budgets, on every mount of the panel —
 * and Firestore has no hard spend cap (spec §12). Messages are loaded for the
 * channel the user actually opens, and nowhere else.
 *
 * One listener per document rather than one query over the collection: a
 * collection query has to satisfy the Firestore rules for every document it
 * could return, and a per-document read is the thing the rules are written
 * against (`canReadChannel`).
 *
 * Returns one unsubscribe function covering all of them.
 */
export async function listenToChannelDocs(session, channelIds, onChannelDoc, onError) {
    if (!session.firestore) {
        throw new ChatUnavailable('chat_not_signed_in');
    }

    const { doc, onSnapshot } = await import(/* webpackChunkName: "firebase" */ 'firebase/firestore');

    const stops = channelIds.map((id) => onSnapshot(
        doc(session.firestore, 'channels', id),
        (snap) => { if (snap.exists()) onChannelDoc(id, snap.data()); },
        (err) => { if (onError) onError(id, new ChatUnavailable('chat_listen_failed', err)); }
    ));

    const stopAll = () => stops.forEach((stop) => { try { stop(); } catch (e) { /* already gone */ } });
    session.unsubscribers.push(stopAll);

    return () => {
        const at = session.unsubscribers.indexOf(stopAll);
        if (at >= 0) session.unsubscribers.splice(at, 1);
        stopAll();
    };
}

/**
 * Listen to a channel's messages.
 *
 * Returns an unsubscribe function. Newest-first from Firestore (that is the
 * only direction an index can serve a "latest N" query), reversed for display,
 * because a chat reads oldest-at-the-top.
 *
 * ⚠ `limit` is capped at MAX_SCROLLBACK whatever the caller asks. The Firestore
 * rules REFUSE a larger query, and a refused listener is a silent empty panel
 * rather than an error — so the cap is applied here where it can be seen.
 */
export async function listenToChannel(session, channelId, limit, onMessages, onError) {
    if (!session.firestore) {
        throw new ChatUnavailable('chat_not_signed_in');
    }

    const { collection, onSnapshot, orderBy, query, limit: fsLimit } =
        await import(/* webpackChunkName: "firebase" */ 'firebase/firestore');

    const take = clampScrollback(limit);

    const q = query(
        collection(session.firestore, 'channels', channelId, 'messages'),
        orderBy('createdAt', 'desc'),
        fsLimit(take)
    );

    const stop = onSnapshot(q,
        (snap) => {
            const rows = [];
            snap.forEach((doc) => rows.push(normaliseMessage(doc.id, doc.data())));
            rows.reverse();
            onMessages(rows);
        },
        (err) => {
            /*
             * A rules refusal and a dropped connection both arrive here. Worth
             * surfacing rather than swallowing: a listener that fails silently
             * is the "chat shows nothing and reports nothing" state, and
             * Firestore will keep retrying a failing listener — which is real
             * money (spec §12: there is no hard spend cap).
             */
            if (onError) onError(new ChatUnavailable('chat_listen_failed', err));
        }
    );

    session.unsubscribers.push(stop);

    return () => {
        const at = session.unsubscribers.indexOf(stop);
        if (at >= 0) session.unsubscribers.splice(at, 1);
        stop();
    };
}

/**
 * Post a message.
 *
 * The 4,000-character limit is the chat service's (spec §5) and it enforces it;
 * checking here as well means the composer can refuse before spending a round
 * trip, and the panel already counts down to the same number.
 */
export async function sendMessage(session, channelId, body) {
    const text = String(body == null ? '' : body).trim();

    if (text === '') throw new ChatUnavailable('empty_message');
    if (text.length > MAX_MESSAGE_CHARS) throw new ChatUnavailable('message_too_long');

    try {
        const res = await chatHttp(session, 'post',
            '/api/channels/' + encodeURIComponent(channelId) + '/messages', { body: text });

        return (res && res.data && res.data.data) || null;
    } catch (e) {
        const code = status(e);

        /*
         * 403 is the interesting one and it is not an error in the panel's
         * sense: outside office hours, outside a job's 24h window, or an
         * archived channel all land here, and the panel should say which rather
         * than "sending failed". The server owns that decision — the panel's
         * own read-only state is a courtesy, not the gate.
         */
        if (code === 403) throw new ChatUnavailable('posting_closed', e);
        if (code === 401) throw new ChatUnavailable('chat_session_expired', e);
        if (code === 422) throw new ChatUnavailable('message_rejected', e);
        if (code === 429) throw new ChatUnavailable('too_many_messages', e);

        throw new ChatUnavailable('chat_unreachable', e);
    }
}

/** The HTTP status of an axios failure, or 0 when the request never landed. */
function status(e) {
    return (e && e.response && e.response.status) || 0;
}
